import { router } from "@inertiajs/react"
import { FormProvider, get } from "react-hook-form"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDown, faArrowUp, faCheck, faDeleteLeft } from "@fortawesome/free-solid-svg-icons"
import { useInertiaForm } from "../hooks/useInertiaForm"
import { FormField } from "./FormField"
import RichMdxEditor from "./RichMdxEditor"
import { useState } from "react"
import CopyLinkButton from "./CopyLinkButton"
import PreviewableVisitorView from "./PreviewableVisitorView"
import { capitalize } from "../utils/strings"
import ReferencesSectionSelector from "./ReferencesSectionSelector"

const today = new Date().toDateString()

export default function PublishablePreview({
  publishable = {},
  path,
  frontendViewPath,
  recordKind,
  referenceSectionOption
}: any) {
  const newPublishable = !publishable || Object.keys(publishable).length === 0
  const [viewMode, setViewMode] = useState('content-edit')

  const cleanBeforeSubmit = (data: any) => {
    const formData = recordKind === 'reference' ? {
      "references_section_id": get(data, 'references_section.value', null),
      ...data
    } : data
    delete formData['references_section']
    return formData
  }

  const {
    formMethods,
    onSubmit,
    isSubmitting,
  } = useInertiaForm({
    key: 'publishable',
    serverData: newPublishable ? {
      title: '',
      content: '',
      published_at: today,
      references_section: referenceSectionOption
    } : {
      title: publishable.title || '',
      content: publishable.content || '',
      published_at: publishable.published_at,
      references_section: publishable?.references_section?.option
    },
    options: {
      url: newPublishable ? path : `${path}/${publishable?.id}`,
      method: newPublishable ? 'post' : 'patch',
    },
    cleanBeforeSubmit
  })

  const {
    formState: { isDirty },
    watch
  } = formMethods

  const publishableData = watch()

  const handlePublish = () => {
    router.patch(`${path}/${publishable?.id}`,
      {
        publishable: {
          ...cleanBeforeSubmit(publishableData),
          published_at: publishableData.published_at || new Date().toISOString(),
        }
      }
    )
  }

  const handleUnpublish = () => {
    if (window.confirm(`Are you sure you want to unpublish ${recordKind}?`)) {
      router.patch(`${path}/${publishable?.id}`,
        {
          publishable: {
            ...cleanBeforeSubmit(publishableData),
            published_at: null,
          }
        }
      )
    }
  }

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete this ${recordKind}?`)) {
      router.delete(`${path}/${publishable?.id}`)
    }
  }

  const handleMoveUp = () => {
    router.post(`${path}/${publishable?.id}/move_up`)
  }

  const handleMoveDown = () => {
    router.post(`${path}/${publishable?.id}/move_down`)
  }

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={onSubmit}
        onKeyDown={(e) => {
          if (e.shiftKey && e.key === 'Enter') {
            e.preventDefault()
            onSubmit(e)
          }
        }}
        style={{
          backgroundColor: 'white',
          height: '100%',
          border: '2px solid #ddd',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{
          width: '100%',
          backgroundColor: '#f5f5f5',
          borderBottom: '2px solid #ddd',
          padding: '4px',
          fontWeight: 'bold',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '4px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '4px',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              {newPublishable ? (
                `New ${capitalize(recordKind)}`
              ) : (
                <>
                  {publishable.published_at ? (
                    <>
                      <a
                        href={frontendViewPath}
                        target="_blank"
                        className="link"
                      >
                        {publishable.title}
                      </a>
                      <CopyLinkButton link={new URL(frontendViewPath, window.location.origin).href} />
                    </>
                  ) : (
                    <span>{publishable.title}</span>
                  )}
                  <div
                    className="tooltip tooltip-bottom cursor-pointer"
                    data-tip="Delete"
                    onClick={handleDelete}
                  >
                    <FontAwesomeIcon icon={faDeleteLeft} />
                  </div>
                  <div
                    className="tooltip tooltip-bottom cursor-pointer"
                    data-tip="Move up"
                    onClick={handleMoveUp}
                  >
                    <FontAwesomeIcon
                      icon={faArrowUp}
                      className="text-sm"
                    />
                  </div>
                  <div
                    className="tooltip tooltip-bottom cursor-pointer"
                    data-tip="Move down"
                    onClick={handleMoveDown}
                  >
                    <FontAwesomeIcon
                      icon={faArrowDown}
                      className="text-sm"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '4px',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            {!newPublishable && (
              <>
                {publishable.published_at ? (
                  <button
                    type="button"
                    className="btn btn-error btn-sm"
                    onClick={handleUnpublish}
                  >
                    Unpublish
                  </button>
                ) : (

                  <button
                    type="button"
                    className="btn btn-primary btn-success btn-sm"
                    onClick={handlePublish}
                  >
                    Publish
                  </button>
                )}
              </>
            )}
            {isDirty && (
              <button
                type="button"
                onClick={() => window.location.reload()}
                disabled={isSubmitting}
                className="btn btn-sm"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="btn btn-sm btn-success"
              disabled={isSubmitting || !isDirty}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
              <FontAwesomeIcon icon={faCheck} />
            </button>
          </div>
        </div>

        <div style={{
          padding: '4px',
          borderBottom: '1px solid #ddd',
          backgroundColor: 'white',
        }}>
          <FormField.Container style={{
            backgroundColor: 'var(--color-base-300)',
            padding: '4px',
            display: 'flex',
            flexDirection: 'row',
          }}>
            <FormField.Field
              name="title"
              label="Title"
              placeholder={`${capitalize(recordKind)} Title`}
              registerProps={{ required: true }}
            />

            {recordKind === 'reference' && (
              <ReferencesSectionSelector
                name="references_section"
              />
            )}

            {(newPublishable || publishable.published_at) && (
              <FormField.DateField
                name="published_at"
                label="Published On"
              />
            )}

            <div style={{
              marginLeft: 'auto',
            }}>
              <FormField.Select
                label="View Mode"
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
              >
                <option disabled={true}>View mode</option>
                <option value="content-edit">Edit Content</option>
                <option value="visitor-view">Preview</option>
              </FormField.Select>
            </div>
          </FormField.Container>
        </div>

        <div style={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
        }}>
          {viewMode === 'content-edit' && (
            <RichMdxEditor
              key={`publishable-${publishable?.id}-content-preview`}
            />
          )}
          {viewMode === 'visitor-view' && (
            <div style={{
              padding: '4px'
            }}>
              <PreviewableVisitorView
                publishable={publishableData}
              />
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
